/**
 * EditManualAmcDialog (Iter 37)
 *
 * Super-Admin-only edit for Manual / Legacy AMC schedules. Edits affect
 * FUTURE invoice generations only — already-persisted invoices are
 * immutable and stay as they were emitted.
 *
 * Backend: PATCH /billing/amc/{schedule_id}  (super_admin)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Trash, Plus, Receipt, Calendar, Buildings, FileText, MagnifyingGlass } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { DialogFooter } from "./ui/dialog";
import FormDialog, { FormDialogCancel } from "./FormDialog";
import CataloguePickerDialog from "./CataloguePickerDialog";
import { toast } from "sonner";

const FREQUENCIES = ["Annual", "Half-Yearly", "Quarterly", "Monthly"];
const GST_RATES = [0, 5, 12, 18, 28];

let _editKey = 0;
const k = () => `eli-${++_editKey}-${Date.now()}`;

const lineFromSched = (li) => ({
    _key: k(),
    id: li.id || null,
    product_name: li.product_name || "",
    description: li.description || "",
    hsn_sac: li.hsn_sac || "997331",
    quantity: li.quantity ?? 1,
    rate: li.rate ?? "",
    gst_percent: li.gst_percent ?? 18,
    catalogue_id: li.catalogue_id || null,
});

const formFromSched = (s) => ({
    amc_name: s?.amc_name || s?.product_name || "",
    billing_frequency: s?.billing_frequency || "Annual",
    billing_day: s?.billing_day ?? "",
    start_date: (s?.start_date || "").slice(0, 10),
    end_date: (s?.end_date || "").slice(0, 10),
    notes: s?.notes || "",
    external_po_number: s?.external_po_number || "",
    external_po_date: (s?.external_po_date || "").slice(0, 10),
    // Iter 51.0.3 — read-only display of the historical REEDOS Proposal No
    // captured at create time. Sourced verbatim from the persisted schedule;
    // never sent back on PATCH (the Edit dialog has no input control for
    // this field, only a disabled display).
    proposal_number: s?.proposal_number || "",
    line_items: (s?.line_items || []).map(lineFromSched),
});

const formatINR = (n) => {
    const num = Number(n) || 0;
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(num);
};

export default function EditManualAmcDialog({ open, onOpenChange, sched, onSaved }) {
    const [form, setForm] = useState(() => formFromSched(sched));
    const [busy, setBusy] = useState(false);
    const baselineRef = useRef(JSON.stringify(formFromSched(sched)));
    // 2026-02-13 — Multi-select catalogue picker (consistent with Create dialog).
    const [catalogue, setCatalogue] = useState([]);
    const [pickerOpen, setPickerOpen] = useState(false);

    useEffect(() => {
        if (!open || !sched) return;
        const next = formFromSched(sched);
        setForm(next);
        baselineRef.current = JSON.stringify(next);
        // Best-effort catalogue fetch — same endpoint as Create dialog. Silently
        // tolerated on failure; the manual line-item path stays functional.
        api.get("/billing/catalogue/list")
            .then((res) => setCatalogue(res.data.items || []))
            .catch(() => { /* picker simply shows "No catalogue items available." */ });
    }, [open, sched?.id]);

    const isDirty = useMemo(
        () => JSON.stringify(form) !== baselineRef.current,
        [form],
    );

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));
    const updateLine = (key, patch) =>
        setForm((f) => ({ ...f, line_items: f.line_items.map((l) => (l._key === key ? { ...l, ...patch } : l)) }));
    const removeLine = (key) =>
        setForm((f) => ({ ...f, line_items: f.line_items.filter((l) => l._key !== key) }));
    const addLine = () =>
        setForm((f) => ({
            ...f,
            line_items: [...f.line_items, {
                _key: k(), id: null, product_name: "", description: "",
                hsn_sac: "997331", quantity: 1, rate: "", gst_percent: 18, catalogue_id: null,
            }],
        }));

    // 2026-02-13 — Multi-select catalogue commit handler. Mirrors the smart-fill
    // semantics of `CreateManualAmcDialog.commitCatalogueSelection`: when the
    // current trailing line is still empty, it is reused for item #1 to avoid
    // a stray blank row; otherwise every selected item is appended.
    const commitCatalogueSelection = (items) => {
        if (!items?.length) return;
        const filled = items.map((item) => ({
            catalogue_id: item.id,
            product_name: item.name,
            // The Edit dialog does NOT carry currency in the form — see
            // `formFromSched`. Rate is captured numerically and rendered in
            // INR by the table; that contract is preserved here.
            rate: item.default_amc_fee || "",
            gst_percent: item.default_gst_percent ?? 18,
            hsn_sac: item.hsn_sac_code || "997331",
        }));
        setForm((f) => {
            const lines = [...f.line_items];
            const lastIdx = lines.length - 1;
            // Smart-fill — reuse trailing empty line for first item.
            if (lastIdx >= 0 && !lines[lastIdx].product_name && !lines[lastIdx].rate) {
                lines[lastIdx] = {
                    ...lines[lastIdx],
                    ...filled[0],
                    quantity: lines[lastIdx].quantity ?? 1,
                };
                for (let i = 1; i < filled.length; i++) {
                    lines.push({
                        _key: k(), id: null, description: "",
                        quantity: 1, ...filled[i],
                    });
                }
            } else {
                for (const f0 of filled) {
                    lines.push({
                        _key: k(), id: null, description: "",
                        quantity: 1, ...f0,
                    });
                }
            }
            return { ...f, line_items: lines };
        });
        toast.success(
            `${filled.length} product${filled.length === 1 ? "" : "s"} added`,
        );
        setPickerOpen(false);
    };

    // "Already added" indicator source for the multi-select picker.
    const existingCatalogueIds = useMemo(
        () => new Set(form.line_items.map((l) => l.catalogue_id).filter(Boolean)),
        [form.line_items],
    );

    const totals = useMemo(() => {
        let sub = 0, gst = 0;
        const rates = new Set();
        for (const l of form.line_items) {
            const amt = (Number(l.quantity) || 0) * (Number(l.rate) || 0);
            const g = (Number(l.gst_percent) || 0) / 100;
            sub += amt;
            gst += amt * g;
            if (l.rate) rates.add(Number(l.gst_percent) || 0);
        }
        return {
            subtotal: Math.round(sub * 100) / 100,
            gst: Math.round(gst * 100) / 100,
            total: Math.round((sub + gst) * 100) / 100,
            mixed: rates.size > 1,
        };
    }, [form.line_items]);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!sched?.id) return;
        if (!form.amc_name.trim()) { toast.error("AMC name is required"); return; }
        if (!form.start_date || !form.end_date) { toast.error("Start and End date are required"); return; }
        if (new Date(form.end_date) < new Date(form.start_date)) {
            toast.error("End date must be on or after Start date"); return;
        }
        const cleanLines = form.line_items
            .map((l) => ({ ...l, product_name: (l.product_name || "").trim() }))
            .filter((l) => l.product_name || Number(l.rate) > 0);
        if (cleanLines.length === 0) { toast.error("Add at least one line item"); return; }
        for (const l of cleanLines) {
            if (!l.product_name) { toast.error("Every line needs a product name"); return; }
            if (Number(l.rate) < 0) { toast.error(`Rate cannot be negative for "${l.product_name}"`); return; }
            if (!Number(l.quantity) || Number(l.quantity) <= 0) { toast.error(`Quantity must be > 0 for "${l.product_name}"`); return; }
        }
        if (totals.subtotal <= 0) { toast.error("At least one line must have a rate greater than ₹0 — an AMC cannot have a zero contract value"); return; }

        setBusy(true);
        try {
            const payload = {
                amc_name: form.amc_name.trim(),
                billing_frequency: form.billing_frequency,
                start_date: form.start_date,
                end_date: form.end_date,
                notes: form.notes || "",
                // Iter 41: persist client PO reference. Empty string clears
                // the field server-side (sentinel handled in routes_amc).
                external_po_number: (form.external_po_number || "").trim(),
                external_po_date: (form.external_po_date || "").trim(),
                line_items: cleanLines.map((l) => ({
                    id: l.id || undefined,
                    product_name: l.product_name,
                    description: l.description || "",
                    hsn_sac: l.hsn_sac || "997331",
                    quantity: Number(l.quantity),
                    rate: Number(l.rate) || 0,
                    gst_percent: Number(l.gst_percent),
                    catalogue_id: l.catalogue_id || null,
                })),
            };
            if (form.billing_day) payload.billing_day = Number(form.billing_day);
            const { data } = await api.patch(`/billing/amc/${sched.id}`, payload);
            toast.success("AMC schedule updated · future invoices will reflect the changes");
            baselineRef.current = JSON.stringify(form);
            onSaved?.(data);
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to save changes"));
        } finally {
            setBusy(false);
        }
    };

    if (!sched) return null;

    return (
        <FormDialog
            open={open}
            onOpenChange={(v) => !busy && onOpenChange(v)}
            isDirty={isDirty}
            contentClassName="max-w-3xl max-h-[90vh] overflow-y-auto"
            testId="edit-manual-amc-dialog"
            srTitle="Edit Manual AMC"
            header={
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                        <Pencil size={18} weight="duotone" className="text-amber-600" />
                        Edit Manual AMC
                    </div>
                    <div className="text-xs text-slate-500">
                        Updates affect <b>future invoice generations only</b>. Already-issued invoices remain unchanged.
                        <span className="block mt-1 text-amber-700">Super Admin only.</span>
                    </div>
                </div>
            }
        >
            <form onSubmit={submit} className="space-y-6 py-2">
                {/* Relationship — client is read-only after creation */}
                <Section icon={<Buildings size={14} weight="duotone" />} title="Relationship">
                    <Field label="Client" wide>
                        <div className="text-sm text-slate-700 px-3 py-2 bg-slate-50 border border-slate-200 rounded">
                            {sched.client?.name || "—"}
                            <span className="ml-2 text-[11px] text-slate-400">(client cannot be reassigned here)</span>
                        </div>
                    </Field>
                    <Field label="AMC Name *" required wide>
                        <Input
                            value={form.amc_name}
                            onChange={(e) => set({ amc_name: e.target.value })}
                            data-testid="edit-amc-name-input"
                        />
                    </Field>
                </Section>

                {/* Schedule */}
                <Section icon={<Calendar size={14} weight="duotone" />} title="Schedule">
                    <Field label="Start Date *" required>
                        <Input type="date" value={form.start_date} onChange={(e) => set({ start_date: e.target.value })} data-testid="edit-amc-start-date" />
                    </Field>
                    <Field label="End Date *" required>
                        <Input type="date" value={form.end_date} onChange={(e) => set({ end_date: e.target.value })} data-testid="edit-amc-end-date" />
                    </Field>
                    <Field label="Frequency *" required>
                        <select
                            value={form.billing_frequency}
                            onChange={(e) => set({ billing_frequency: e.target.value })}
                            className="w-full h-9 px-3 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                            data-testid="edit-amc-frequency"
                        >
                            {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </Field>
                    <Field label="Billing Day" hint="Day of month (1–28). Leave blank to bill on Start Date.">
                        <Input
                            type="number" min={1} max={28}
                            value={form.billing_day}
                            onChange={(e) => set({ billing_day: e.target.value })}
                            data-testid="edit-amc-billing-day"
                        />
                    </Field>
                </Section>

                {/* Iter 41: Client PO (external reference) */}
                <Section icon={<FileText size={14} weight="duotone" />} title="Client PO reference (optional)">
                    <Field label="Client PO Number" hint="Customer-issued PO number. Used by finance for invoice reconciliation. Distinct from any internal/system PO.">
                        <Input
                            value={form.external_po_number}
                            onChange={(e) => set({ external_po_number: e.target.value })}
                            placeholder="e.g. CLIENT/PO/2026/0042"
                            maxLength={80}
                            data-testid="edit-amc-client-po-number"
                        />
                    </Field>
                    <Field label="Client PO Date">
                        <Input
                            type="date"
                            value={form.external_po_date}
                            onChange={(e) => set({ external_po_date: e.target.value })}
                            data-testid="edit-amc-client-po-date"
                        />
                    </Field>
                    {/* Iter 51.0.3 — historical REEDOS Proposal No read-only
                        display. Captured once at AMC create-time; intentionally
                        immutable post-create per operational directive.
                        Renders an em-dash placeholder when not set. */}
                    <Field
                        label="REEDOS Proposal No"
                        hint="Historical reference captured at AMC creation — immutable. To correct a typo, the AMC must be recreated."
                    >
                        <Input
                            value={form.proposal_number || ""}
                            readOnly
                            disabled
                            placeholder="— (not set at creation)"
                            className="bg-slate-50 text-slate-600 cursor-not-allowed"
                            data-testid="edit-amc-proposal-number-readonly"
                        />
                    </Field>
                </Section>

                {/* Line items */}
                <div className="space-y-3" data-testid="edit-amc-line-items-section">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="reedos-eyebrow flex items-center gap-1.5 text-[10px]">
                                <Receipt size={12} weight="duotone" /> <span>Line Items</span>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-1 leading-snug">
                                Rates capture the <b className="text-slate-700">annual contract value</b> per line.
                                {form.billing_frequency !== "Annual" && (
                                    <> {" "}<span className="text-slate-700">{form.billing_frequency}</span> billing is derived automatically.</>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button" variant="outline" size="sm"
                                onClick={() => setPickerOpen(true)}
                                className="gap-1.5 text-xs"
                                data-testid="edit-amc-add-from-catalogue-btn"
                            >
                                <MagnifyingGlass size={12} weight="bold" /> Add from catalogue
                            </Button>
                            <Button
                                type="button" variant="outline" size="sm"
                                onClick={addLine}
                                className="gap-1.5 text-xs"
                                data-testid="edit-amc-add-line-btn"
                            >
                                <Plus size={12} weight="bold" /> Add line
                            </Button>
                        </div>
                    </div>

                    <div className="border border-slate-200 rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-3 py-2 reedos-eyebrow text-[10px] w-[26%]">Product / Service</th>
                                    <th className="px-2 py-2 reedos-eyebrow text-[10px] w-[7%] text-right">Qty</th>
                                    <th className="px-2 py-2 reedos-eyebrow text-[10px] w-[18%] text-right">Rate (annual)</th>
                                    <th className="px-2 py-2 reedos-eyebrow text-[10px] w-[9%]">GST</th>
                                    <th className="px-2 py-2 reedos-eyebrow text-[10px] w-[12%]">HSN/SAC</th>
                                    <th className="px-3 py-2 reedos-eyebrow text-[10px] w-[18%] text-right">Annual line total</th>
                                    <th className="px-2 py-2 w-8"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {form.line_items.map((l, idx) => {
                                    const lineTotal = (Number(l.quantity) || 0) * (Number(l.rate) || 0);
                                    return (
                                        <tr key={l._key} className="border-t border-slate-100 align-top" data-testid={`edit-amc-line-${idx}`}>
                                            <td className="px-3 py-2">
                                                <Input
                                                    value={l.product_name}
                                                    onChange={(e) => updateLine(l._key, { product_name: e.target.value, catalogue_id: null })}
                                                    className="h-8 text-sm"
                                                    data-testid={`edit-amc-line-name-${idx}`}
                                                />
                                                {l.catalogue_id && (
                                                    <div className="text-[9px] text-emerald-700 mt-0.5 font-mono">📌 {l.catalogue_id} (snapshot)</div>
                                                )}
                                                <Input
                                                    value={l.description}
                                                    onChange={(e) => updateLine(l._key, { description: e.target.value })}
                                                    placeholder="Optional sub-description"
                                                    className="h-7 mt-1 text-xs text-slate-600"
                                                    data-testid={`edit-amc-line-desc-${idx}`}
                                                />
                                            </td>
                                            <td className="px-2 py-2 text-right">
                                                <Input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={l.quantity}
                                                    onChange={(e) => updateLine(l._key, { quantity: e.target.value.replace(/[^\d.]/g, "") })}
                                                    className="h-8 text-sm text-right"
                                                    data-testid={`edit-amc-line-qty-${idx}`}
                                                />
                                            </td>
                                            <td className="px-2 py-2 text-right">
                                                <Input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={l.rate}
                                                    onChange={(e) => updateLine(l._key, { rate: e.target.value.replace(/[^\d.]/g, "") })}
                                                    placeholder="Included"
                                                    className="h-8 text-sm text-right font-mono placeholder:text-emerald-600/70"
                                                    data-testid={`edit-amc-line-rate-${idx}`}
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                <select
                                                    value={l.gst_percent}
                                                    onChange={(e) => updateLine(l._key, { gst_percent: Number(e.target.value) })}
                                                    className="w-full h-8 px-2 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                                                    data-testid={`edit-amc-line-gst-${idx}`}
                                                >
                                                    {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                                                </select>
                                            </td>
                                            <td className="px-2 py-2">
                                                <Input
                                                    value={l.hsn_sac}
                                                    onChange={(e) => updateLine(l._key, { hsn_sac: e.target.value })}
                                                    placeholder="e.g. 998314"
                                                    className="h-8 text-sm font-mono"
                                                    data-testid={`edit-amc-line-hsn-${idx}`}
                                                />
                                            </td>
                                            <td className="px-3 py-2 text-right reedos-data font-medium whitespace-nowrap">
                                                {Number(l.rate) > 0
                                                    ? formatINR(lineTotal)
                                                    : <span className="text-emerald-700 text-[11px] font-semibold" data-testid={`edit-amc-line-included-${idx}`}>Included — ₹0</span>}
                                            </td>
                                            <td className="px-2 py-2 text-right">
                                                {form.line_items.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeLine(l._key)}
                                                        className="text-slate-400 hover:text-rose-600 transition-colors"
                                                        data-testid={`edit-amc-line-remove-${idx}`}
                                                        aria-label="Remove line"
                                                    >
                                                        <Trash size={14} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot className="bg-slate-50 border-t border-slate-200 text-sm" data-testid="edit-amc-totals">
                                <tr>
                                    <td colSpan={5} className="px-3 py-2 text-right text-slate-600">Subtotal · annual</td>
                                    <td className="px-3 py-2 text-right reedos-data font-semibold whitespace-nowrap">{formatINR(totals.subtotal)}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td colSpan={5} className="px-3 py-1.5 text-right text-slate-600 text-xs">
                                        GST {totals.mixed && <span className="text-amber-700 font-medium">(Mixed rates)</span>}
                                    </td>
                                    <td className="px-3 py-1.5 text-right reedos-data text-xs whitespace-nowrap">{formatINR(totals.gst)}</td>
                                    <td></td>
                                </tr>
                                <tr className="border-t border-slate-200">
                                    <td colSpan={5} className="px-3 py-2 text-right font-semibold text-slate-900">Annual contract value</td>
                                    <td className="px-3 py-2 text-right reedos-data font-bold text-base text-slate-900 whitespace-nowrap" data-testid="edit-amc-total">
                                        {formatINR(totals.total)}
                                    </td>
                                    <td></td>
                                </tr>
                                {(() => {
                                    const invPerYear = { Annual: 1, "Half-Yearly": 2, Quarterly: 4, Monthly: 12 }[form.billing_frequency] || 1;
                                    if (invPerYear === 1) return null;
                                    const periodic = totals.total / invPerYear;
                                    const freqLabel = form.billing_frequency.toLowerCase();
                                    return (
                                        <tr className="border-t border-slate-100 bg-slate-100/50">
                                            <td colSpan={5} className="px-3 py-2 text-right text-[11px] text-slate-600">
                                                Each {freqLabel} invoice ({invPerYear}/year)
                                            </td>
                                            <td className="px-3 py-2 text-right reedos-data text-slate-700 font-semibold text-sm whitespace-nowrap" data-testid="edit-amc-periodic">
                                                {formatINR(periodic)}
                                            </td>
                                            <td></td>
                                        </tr>
                                    );
                                })()}
                            </tfoot>
                        </table>
                    </div>
                </div>

                {/* Notes */}
                <div>
                    <Label className="text-[11px] reedos-eyebrow text-slate-600">Notes</Label>
                    <textarea
                        rows={2}
                        value={form.notes}
                        onChange={(e) => set({ notes: e.target.value })}
                        className="w-full mt-1 px-3 py-2 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                        data-testid="edit-amc-notes"
                    />
                </div>

                <DialogFooter className="border-t border-slate-100 -mx-6 px-6 pt-4 mt-2">
                    <FormDialogCancel disabled={busy} testId="edit-amc-cancel-btn" />
                    <Button type="submit" disabled={busy} data-testid="edit-amc-save-btn" className="bg-amber-600 hover:bg-amber-500 text-white">
                        {busy ? "Saving…" : "Save changes"}
                    </Button>
                </DialogFooter>
            </form>

            {/* ── Catalogue picker (shared multi-select, 2026-02-13) ── */}
            <CataloguePickerDialog
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                catalogue={catalogue}
                existingCatalogueIds={existingCatalogueIds}
                formatPrice={(n) => formatINR(n)}
                onAddSelected={commitCatalogueSelection}
            />
        </FormDialog>
    );
}

function Section({ icon, title, children }) {
    return (
        <div className="space-y-3">
            <div className="reedos-eyebrow flex items-center gap-1.5 text-[10px]">
                {icon}<span>{title}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
        </div>
    );
}

function Field({ label, hint, required, wide, children }) {
    return (
        <div className={wide ? "sm:col-span-2" : ""}>
            <Label className="text-[11px] reedos-eyebrow text-slate-600">
                {label}
                {required && <span className="text-rose-500 ml-0.5">*</span>}
            </Label>
            <div className="mt-1">{children}</div>
            {hint && <div className="text-[10px] text-slate-500 mt-1 leading-snug">{hint}</div>}
        </div>
    );
}
